import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ShareResponse } from '@chromarble/shared-types';
import { errorMessage } from '../services/api';
import { useOnline, useRealtimeStatus } from '../hooks/realtime';

export function Screen({ title, back, children, actions }: { title: string; back?: string | true; children: ReactNode; actions?: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="screen">
      <header className="topbar">
        {back ? (
          <button className="icon-btn" aria-label="뒤로" onClick={() => (back === true ? navigate(-1) : navigate(back))}>
            ←
          </button>
        ) : (
          <Link to="/" className="brand" aria-label="홈">
            ◆
          </Link>
        )}
        <h1>{title}</h1>
        <div className="topbar-actions">{actions}</div>
      </header>
      <main className="content">{children}</main>
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="error" role="alert">
      {typeof error === 'string' ? error : errorMessage(error)}
    </p>
  );
}

export function ConnectionBadge() {
  const online = useOnline();
  const status = useRealtimeStatus();
  const state = !online ? 'offline' : status === 'open' ? 'online' : 'reconnecting';
  const label = state === 'online' ? '연결됨' : state === 'offline' ? '오프라인' : '재연결 중';
  return (
    <span className={`conn conn-${state}`} data-testid="connection-status" data-state={state}>
      <span className="dot" /> {label}
    </span>
  );
}

/**
 * Share preview: shows exactly what will be shared and with whom before anything leaves
 * the device. Never claims a Crew chat delivery unless the server confirmed it.
 */
export function ShareSheet({ load, scopeNote }: { load: () => Promise<ShareResponse>; scopeNote: string }) {
  const [share, setShare] = useState<ShareResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      setShare(await load());
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.text);
      setCopied(true);
    } catch {
      setError('클립보드 복사가 차단되었습니다. 아래 내용을 길게 눌러 복사하세요.');
    }
  };

  const nativeShare = async () => {
    if (!share) return;
    try {
      await navigator.share({ title: share.title, text: share.text });
    } catch {
      /* user cancelled */
    }
  };

  if (!share) {
    return (
      <div className="share">
        <button className="btn secondary" onClick={open} disabled={busy} data-testid="share-open">
          {busy ? '준비 중…' : 'Crew로 공유'}
        </button>
        <ErrorNote error={error} />
      </div>
    );
  }
  return (
    <div className="share share-open" data-testid="share-sheet">
      <p className="muted small">
        {share.delivered ? 'Crew 채팅방에 전송되었습니다.' : `Crew 채팅으로 자동 전송되지 않았습니다. ${share.reason ?? ''}`}
      </p>
      <p className="muted small">공유 범위: {scopeNote}</p>
      <pre className="share-text" data-testid="share-text">{share.text}</pre>
      <div className="row">
        <button className="btn" onClick={copy} data-testid="share-copy">
          {copied ? '복사됨 ✓' : '메시지 복사'}
        </button>
        {'share' in navigator && (
          <button className="btn secondary" onClick={nativeShare}>
            다른 앱으로 공유
          </button>
        )}
      </div>
      <ErrorNote error={error} />
    </div>
  );
}

export function formatScore(n: number): string {
  return n.toLocaleString('ko-KR');
}

export function pct(rate: number): string {
  return `${Math.round(rate * 1000) / 10}%`;
}

export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
